import type {RumEventDomainContext} from '@datadog/browser-rum'

const MAX_SEMANTIC_PATH_DEPTH = 6
const MAX_ROLE_INDEX = 50
const MAX_ROLE_SIBLINGS_SCANNED = 200
// Shared across one target's path so deep role-less trees cannot force unbounded DOM walks.
const MAX_SEMANTIC_VISITS = 1_000
let semanticVisitsRemaining = 0
interface SemanticActionTarget {
  path?: string
}
const semanticActionTargets = new WeakMap<Event, SemanticActionTarget | undefined>()
const ALLOWED_EXPLICIT_ROLES = new Set([
  'application',
  'article',
  'banner',
  'button',
  'cell',
  'checkbox',
  'columnheader',
  'combobox',
  'complementary',
  'contentinfo',
  'dialog',
  'feed',
  'form',
  'grid',
  'gridcell',
  'group',
  'heading',
  'link',
  'list',
  'listbox',
  'listitem',
  'main',
  'menu',
  'menubar',
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'navigation',
  'option',
  'radio',
  'radiogroup',
  'region',
  'row',
  'rowgroup',
  'rowheader',
  'search',
  'searchbox',
  'slider',
  'spinbutton',
  'switch',
  'tab',
  'table',
  'tablist',
  'textbox',
  'tree',
  'treegrid',
  'treeitem',
])
const OTHER_RECOGNIZED_ROLES = new Set([
  'alert',
  'alertdialog',
  'blockquote',
  'caption',
  'code',
  'definition',
  'deletion',
  'directory',
  'document',
  'emphasis',
  'figure',
  'generic',
  'img',
  'insertion',
  'log',
  'mark',
  'marquee',
  'math',
  'meter',
  'none',
  'note',
  'paragraph',
  'presentation',
  'progressbar',
  'scrollbar',
  'separator',
  'status',
  'strong',
  'subscript',
  'superscript',
  'tabpanel',
  'term',
  'time',
  'timer',
  'toolbar',
  'tooltip',
  'doc-abstract',
  'doc-acknowledgments',
  'doc-afterword',
  'doc-appendix',
  'doc-backlink',
  'doc-biblioentry',
  'doc-bibliography',
  'doc-biblioref',
  'doc-chapter',
  'doc-colophon',
  'doc-conclusion',
  'doc-cover',
  'doc-credit',
  'doc-credits',
  'doc-dedication',
  'doc-endnote',
  'doc-endnotes',
  'doc-epigraph',
  'doc-epilogue',
  'doc-errata',
  'doc-example',
  'doc-footnote',
  'doc-foreword',
  'doc-glossary',
  'doc-glossref',
  'doc-index',
  'doc-introduction',
  'doc-noteref',
  'doc-notice',
  'doc-pagebreak',
  'doc-pagelist',
  'doc-part',
  'doc-preface',
  'doc-prologue',
  'doc-pullquote',
  'doc-qna',
  'doc-subtitle',
  'doc-tip',
  'doc-toc',
  'graphics-document',
  'graphics-object',
  'graphics-symbol',
])
const ALLOWED_ARIA_STATES: ReadonlyArray<readonly [string, string, ReadonlySet<string>]> = [
  ['aria-checked', 'checked', new Set(['false', 'mixed', 'true'])],
  ['aria-current', 'current', new Set(['date', 'false', 'location', 'page', 'step', 'time', 'true'])],
  ['aria-disabled', 'disabled', new Set(['false', 'true'])],
  ['aria-expanded', 'expanded', new Set(['false', 'true'])],
  ['aria-haspopup', 'haspopup', new Set(['dialog', 'false', 'grid', 'listbox', 'menu', 'tree', 'true'])],
  ['aria-pressed', 'pressed', new Set(['false', 'mixed', 'true'])],
  ['aria-selected', 'selected', new Set(['false', 'true'])],
]

function spendSemanticVisit(): boolean {
  if (semanticVisitsRemaining <= 0) return false
  semanticVisitsRemaining--
  return true
}

function isWithinSectioningContent(element: Element): boolean {
  let ancestor = getSemanticParent(element)
  while (ancestor) {
    // Out of budget: treat as sectioned so no landmark role is claimed.
    if (!spendSemanticVisit()) return true
    if (ancestor.matches('article, aside, main, nav, section')) return true
    ancestor = getSemanticParent(ancestor)
  }
  return false
}

function getImplicitRole(element: Element): string | undefined {
  switch (element.tagName) {
    case 'A':
    case 'AREA':
      return element.hasAttribute('href') ? 'link' : undefined
    case 'ARTICLE':
      return 'article'
    case 'ASIDE':
      return 'complementary'
    case 'BUTTON':
    case 'SUMMARY':
      return 'button'
    case 'DIALOG':
      return 'dialog'
    case 'FIELDSET':
    case 'DETAILS':
      return 'group'
    case 'FOOTER':
      return isWithinSectioningContent(element) ? undefined : 'contentinfo'
    case 'HEADER':
      return isWithinSectioningContent(element) ? undefined : 'banner'
    case 'H1':
    case 'H2':
    case 'H3':
    case 'H4':
    case 'H5':
    case 'H6':
      return 'heading'
    case 'INPUT': {
      const type = (element as HTMLInputElement).type
      if (type === 'checkbox') return 'checkbox'
      if (type === 'radio') return 'radio'
      if (type === 'range') return 'slider'
      if (type === 'number') return 'spinbutton'
      if (type === 'button' || type === 'image' || type === 'reset' || type === 'submit') return 'button'
      if (type === 'search') return element.hasAttribute('list') ? 'combobox' : 'searchbox'
      if (type === 'email' || type === 'tel' || type === 'text' || type === 'url') {
        return element.hasAttribute('list') ? 'combobox' : 'textbox'
      }
      return undefined
    }
    case 'LI':
      return 'listitem'
    case 'MAIN':
      return 'main'
    case 'MENU':
    case 'OL':
    case 'UL':
      return 'list'
    case 'NAV':
      return 'navigation'
    case 'OPTION':
      return 'option'
    case 'SELECT':
      return element.hasAttribute('multiple') || Number(element.getAttribute('size')) > 1 ? 'listbox' : 'combobox'
    case 'TABLE':
      return 'table'
    case 'TD':
      return 'cell'
    case 'TEXTAREA':
      return 'textbox'
    case 'TH':
      return element.getAttribute('scope') === 'row' ? 'rowheader' : 'columnheader'
    case 'TR':
      return 'row'
    default:
      return undefined
  }
}

function getSemanticRole(element: Element): string | undefined {
  const explicitRoles = element.getAttribute('role')?.toLowerCase().split(/\s+/)
  const explicitRole = explicitRoles?.find(role => ALLOWED_EXPLICIT_ROLES.has(role) || OTHER_RECOGNIZED_ROLES.has(role))
  if (explicitRole) return ALLOWED_EXPLICIT_ROLES.has(explicitRole) ? explicitRole : undefined
  return getImplicitRole(element)
}

function getSemanticParent(element: Element): Element | null {
  if (element.parentElement) return element.parentElement
  const root = element.getRootNode()
  return root instanceof ShadowRoot ? root.host : null
}

function getSemanticStates(element: Element, role: string): string[] {
  const states: string[] = []

  for (const [attribute, state, allowedValues] of ALLOWED_ARIA_STATES) {
    const value = element.getAttribute(attribute)
    if (value && allowedValues.has(value)) states.push(`${state}=${value}`)
  }

  if (!element.hasAttribute('aria-disabled') && element.matches(':disabled')) states.push('disabled=true')
  if (!element.hasAttribute('aria-checked') && element instanceof HTMLInputElement) {
    if (element.type === 'checkbox' || element.type === 'radio') {
      const checked = element.type === 'checkbox' && element.indeterminate ? 'mixed' : element.checked
      states.push(`checked=${checked}`)
    }
  }
  if (!element.hasAttribute('aria-selected') && element instanceof HTMLOptionElement) {
    states.push(`selected=${element.selected}`)
  }
  if (
    !element.hasAttribute('aria-expanded') &&
    element instanceof HTMLElement &&
    element.tagName === 'SUMMARY' &&
    element.parentElement instanceof HTMLDetailsElement
  ) {
    states.push(`expanded=${element.parentElement.open}`)
  }
  if (role === 'heading' && /^H[1-6]$/.test(element.tagName)) states.push(`level=${element.tagName.slice(1)}`)

  return states
}

function getRoleIndex(element: Element, role: string): number | string | undefined {
  const root = element.getRootNode()
  const siblings = element.parentElement?.children ?? (root instanceof ShadowRoot ? root.children : undefined)
  if (!siblings) return undefined

  let count = 0
  let position = 0
  let scanned = 0
  for (const sibling of siblings) {
    if (!spendSemanticVisit()) return undefined
    if (scanned++ >= MAX_ROLE_SIBLINGS_SCANNED) {
      // Target is beyond the scan limit; otherwise it is treated as lone if no second match was seen.
      if (!position) return `${MAX_ROLE_INDEX}+`
      break
    }
    if (getSemanticRole(sibling) !== role) continue
    count++
    if (sibling === element) position = count
    if (count > MAX_ROLE_INDEX) break
    if (position && count >= 2) break
  }
  if (count < 2) return undefined
  return position && position <= MAX_ROLE_INDEX ? position : `${MAX_ROLE_INDEX}+`
}

function getSemanticActionTargetForElement(target: Element): SemanticActionTarget | undefined {
  const segments: string[] = []
  let element: Element | null = target
  semanticVisitsRemaining = MAX_SEMANTIC_VISITS
  while (element && segments.length < MAX_SEMANTIC_PATH_DEPTH && spendSemanticVisit()) {
    const role = getSemanticRole(element)
    if (role) {
      const index = getRoleIndex(element, role)
      const states = getSemanticStates(element, role)
      segments.unshift(`${role}${index ? `[${index}]` : ''}${states.length ? `[${states.join(',')}]` : ''}`)
    }
    element = getSemanticParent(element)
  }

  const path = segments.length ? segments.join(' > ') : undefined
  return path ? {path} : undefined
}

function getEventTarget(event: Event): Element | undefined {
  const composedPath = event.composedPath()
  return (composedPath.length ? composedPath : [event.target]).find(
    (eventTarget): eventTarget is Element => eventTarget instanceof Element,
  )
}

export function trackSemanticActionTargets(isSessionTracked: () => boolean): () => void {
  let pressedTarget: {pointerId: number; target: SemanticActionTarget | undefined} | undefined
  const listener = (event: PointerEvent) => {
    if (event.isPrimary === false) return

    if (event.type === 'pointerdown') {
      pressedTarget = undefined
      if (!isSessionTracked()) return
      const target = getEventTarget(event)
      pressedTarget = {
        pointerId: event.pointerId,
        target: target ? getSemanticActionTargetForElement(target) : undefined,
      }
    } else {
      const matchesPress = pressedTarget?.pointerId === event.pointerId
      if (event.type === 'pointerup') {
        // Datadog names the pointerdown target but supplies pointerup in beforeSend.
        semanticActionTargets.set(event, matchesPress ? pressedTarget?.target : undefined)
      }
      if (matchesPress) pressedTarget = undefined
    }
  }

  window.addEventListener('pointerdown', listener, {capture: true})
  window.addEventListener('pointerup', listener, {capture: true})
  window.addEventListener('pointercancel', listener, {capture: true})
  return () => {
    window.removeEventListener('pointerdown', listener, {capture: true})
    window.removeEventListener('pointerup', listener, {capture: true})
    window.removeEventListener('pointercancel', listener, {capture: true})
    pressedTarget = undefined
  }
}

export function getSemanticActionTarget(domainContext: RumEventDomainContext): SemanticActionTarget | undefined {
  if (!('events' in domainContext)) return undefined

  for (const event of domainContext.events ?? []) {
    const capturedTarget = semanticActionTargets.get(event)
    if (capturedTarget) return capturedTarget
    if (semanticActionTargets.has(event)) continue

    const target = getEventTarget(event)
    if (!target) continue

    const semanticTarget = getSemanticActionTargetForElement(target)
    if (semanticTarget) return semanticTarget
  }

  return undefined
}
